/**
 * Supabase Browser Client for Web-v2
 *
 * Story: 0-2-supabase-auth-setup
 *
 * This module provides a singleton Supabase client for browser use.
 * Uses the anon/public key which is safe for frontend exposure.
 * All database access is subject to Row Level Security (RLS) policies.
 */

import { createClient } from '@supabase/supabase-js';
import type { SupabaseClient, AuthChangeEvent, Session } from '@supabase/supabase-js';
import { captureStructuredLog, OBSERVABILITY_EVENTS } from '@/lib/observability';

// Get environment variables (Vite uses import.meta.env)
const rawSupabaseUrl: unknown = import.meta.env.VITE_SUPABASE_URL;
const rawSupabaseAnonKey: unknown = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabaseUrl = typeof rawSupabaseUrl === 'string' ? rawSupabaseUrl : '';
const supabaseAnonKey = typeof rawSupabaseAnonKey === 'string' ? rawSupabaseAnonKey : '';
const STORAGE_PROBE_KEY = '__couponhub_supabase_storage_probe__';
let hasRunStorageProbe = false;

// Validate configuration
if (supabaseUrl.length === 0 || supabaseAnonKey.length === 0) {
  console.error(
    'Missing Supabase configuration. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env.local or your deployment environment variables.'
  );
}

/**
 * Supabase Browser Client
 *
 * Uses the anon/public key which is safe for frontend.
 * Session is automatically persisted in localStorage.
 *
 * Configuration:
 * - persistSession: true (default) - sessions stored in localStorage
 * - autoRefreshToken: true (default) - tokens refresh automatically
 */
export const supabase = createClient(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
    },
  }
);

function getErrorCode(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'name' in error) {
    const name = (error as { name?: unknown }).name;
    if (typeof name === 'string' && name.length > 0) {
      return name;
    }
  }

  return 'AUTH_REFRESH_ERROR';
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.length > 0) {
    return error.message;
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.length > 0) {
      return message;
    }
  }

  return 'Unknown auth refresh failure';
}

function captureAuthRefreshFailure(error: unknown): void {
  const errorCode = getErrorCode(error);
  captureStructuredLog(
    OBSERVABILITY_EVENTS.LOG_AUTH_REFRESH_FAILED,
    {
      auth_event: 'get_session',
      error_code: errorCode,
      message: getErrorMessage(error),
      user_id: null,
    },
    {
      dedupeKey: `web-auth-refresh-failed:get_session:${errorCode}`,
      level: 'warning',
    },
  );
}

export function runSupabaseStorageProbe(): void {
  if (hasRunStorageProbe) {
    return;
  }
  hasRunStorageProbe = true;

  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_PROBE_KEY, '1');
    window.localStorage.removeItem(STORAGE_PROBE_KEY);
  } catch (error) {
    const errorCode = getErrorCode(error);
    captureStructuredLog(
      OBSERVABILITY_EVENTS.LOG_STORAGE_PROBE_FAILED,
      {
        storage_provider: 'localStorage',
        error_code: errorCode,
        message: getErrorMessage(error),
      },
      {
        dedupeKey: `web-storage-probe-failed:localStorage:${errorCode}`,
        level: 'warning',
      },
    );
  }
}

/**
 * Subscribe to auth state changes
 *
 * @param callback - Function called when auth state changes
 * @returns Unsubscribe function
 *
 * @example
 * ```ts
 * const { data: { subscription } } = onAuthStateChange((event, session) => {
 *   if (event === 'SIGNED_IN') {
 *     // User signed in
 *   } else if (event === 'SIGNED_OUT') {
 *     // User signed out
 *   }
 * });
 *
 * // Later, to unsubscribe:
 * subscription.unsubscribe();
 * ```
 */
export function onAuthStateChange(
  callback: (event: AuthChangeEvent, session: Session | null) => void
) {
  return supabase.auth.onAuthStateChange(callback);
}

/**
 * Get current session
 *
 * @returns Current session or null if not authenticated
 */
export async function getSession() {
  try {
    const result = await supabase.auth.getSession();
    if (result.error) {
      captureAuthRefreshFailure(result.error);
    }
    return result;
  } catch (error) {
    console.error('[Supabase] getSession failed:', error);
    captureAuthRefreshFailure(error);
    return { data: { session: null }, error } as const;
  }
}

/**
 * Get current user
 *
 * @returns Current user or null if not authenticated
 */
export async function getUser() {
  try {
    const result = await supabase.auth.getUser();
    return result?.data?.user ?? null;
  } catch (error) {
    console.error('[Supabase] getUser failed:', error);
    return null;
  }
}

// Re-export types for convenience
export type { SupabaseClient, AuthChangeEvent, Session };
